// Data structure › Templates (spec/dashboard.md section 5.1): every template of the branch — own, package, component,
// singleton, inline and profile kinds alike (Q-D-13) — with the filters kind, namespace, base, flags and system, the
// search on the server, and the row actions Open editor, Open documents, Clone, Delete.
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ops } from '../api/operations';
import type { Template } from '../api/types';
import { KIND_LABEL, TEMPLATE_FLAGS } from '../content/schema';
import { isSystemTemplate, routes } from '../shell/routes';
import { Button, IconButton } from '../ui/Button';
import { EmptyState } from '../ui/Banner';
import { Badge } from '../ui/Chip';
import { DataGrid } from '../ui/DataGrid';
import { applyRules, FilterBar, useUrlFilter, type FilterField } from '../ui/FilterBar';
import { AlertCircle, Layers, MoreVertical, Plus, Search } from '../ui/icons';
import { Menu } from '../ui/Popover';
import { useQuery } from '../workspace/cache';
import { useWorkspaceVersion } from '../workspace/react';
import { copyName, SchemaTabs, useSchemaEnv, useTemplateActions } from './schema/common';
import './schema/schema.css';

const namespaceOf = (name: string) => (name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : '');

function valueOf(t: Template, field: string): unknown {
  switch (field) {
    case 'namespace':
      return namespaceOf(t.name);
    case 'system':
      return String(isSystemTemplate(t.name));
    case 'documentCount':
      return Number(t.documentCount);
    default:
      return (t as unknown as Record<string, unknown>)[field];
  }
}

export function TemplatesPage() {
  const { client, path, catalog, workspace, navigate, canWrite, org, gameSlug, gameId, branch } = useSchemaEnv();
  useWorkspaceVersion(workspace);
  const actions = useTemplateActions();
  const [search, setSearch] = useState('');
  const [rules, setRules] = useUrlFilter();
  const q = useQuery(`branch:${gameId}:${branch}:templates:list:${search}`, () => client.all<Template>((cursor) => client.data(ops.listTemplates, { path, query: { limit: 200, cursor, search: search || undefined } })), { head: workspace.head });
  const all = q.data ?? [];
  const rows = useMemo(() => applyRules(all, rules, valueOf), [all, rules]);

  const fields = useMemo<FilterField[]>(() => {
    const source = catalog.templateList.length > 0 ? catalog.templateList : all;
    const namespaces = Array.from(new Set(source.map((t) => namespaceOf(t.name)))).sort();
    const bases = Array.from(new Set(source.map((t) => t.parent).filter((p): p is string => !!p))).sort();
    return [
      { key: 'kind', label: 'Kind', type: 'enum', group: 'Template', options: (Object.keys(KIND_LABEL) as Template['kind'][]).map((k) => ({ value: k, label: KIND_LABEL[k] })) },
      { key: 'namespace', label: 'Namespace', type: 'enum', group: 'Template', options: namespaces.map((n) => ({ value: n, label: n || '(none)' })) },
      { key: 'parent', label: 'Base template', type: 'enum', group: 'Template', options: bases.map((b) => ({ value: b, label: b })) },
      { key: 'flags', label: 'Flag', type: 'tag', group: 'Template', options: TEMPLATE_FLAGS.map((f) => ({ value: f.value, label: f.label })) },
      { key: 'system', label: 'System namespace', type: 'bool', group: 'Template' },
      { key: 'documentCount', label: 'Documents', type: 'number', group: 'Template' },
      { key: 'name', label: 'Name', type: 'string', group: 'Template' },
    ];
  }, [catalog.templateList, all]);

  return (
    <section className="schema-page">
      <div className="page-head">
        <h1>Data structure</h1>
        {!q.loading && <span className="page-meta">{all.length} template{all.length === 1 ? '' : 's'}</span>}
        <span className="spacer" />
        <Button variant="primary" disabled={!canWrite} onClick={() => navigate(routes.templateNew(org, gameSlug, branch))} icon={<Plus aria-hidden="true" />}>
          New template
        </Button>
      </div>
      <SchemaTabs />
      <DataGrid<Template>
        id="templates"
        rows={rows}
        rowKey={(t) => t.id}
        loading={q.loading}
        caption={`${rows.length} template${rows.length === 1 ? '' : 's'}${rules.length > 0 ? ' (filtered client-side)' : ''}`}
        toolbar={<FilterBar id="templates" fields={fields} rules={rules} onChange={setRules} />}
        search={{ value: search, onSearch: setSearch, placeholder: 'Search templates by name or display name…' }}
        pageResetKey={JSON.stringify(rules)}
        onRowOpen={(t) => navigate(routes.template(org, gameSlug, branch, t.name))}
        emptyState={
          q.error ? (
            <EmptyState icon={<AlertCircle />} title="The templates did not load" body={String(q.error instanceof Error ? q.error.message : q.error)} action={<Button size="s" onClick={() => q.refetch()}>Retry</Button>} />
          ) : search || rules.length > 0 ? (
            <EmptyState icon={<Search />} title="No template matches" body="Change the search or remove a filter to see more templates." />
          ) : (
            <EmptyState
              icon={<Layers />}
              title="No templates on this branch yet"
              body="A template defines a kind of document; the importer brings a Balancy project's templates."
              action={canWrite ? <Button size="s" icon={<Plus aria-hidden="true" />} onClick={() => navigate(routes.templateNew(org, gameSlug, branch))}>New template</Button> : undefined}
            />
          )
        }
        columns={[
          { key: 'displayName', header: 'Template', fixed: true, render: (t) => <Link to={routes.template(org, gameSlug, branch, t.name)}>{t.displayName ?? t.name}</Link>, cellText: (t) => t.displayName ?? t.name, sortValue: (t) => t.displayName ?? t.name },
          { key: 'name', header: 'Name', render: (t) => <code>{t.name}</code>, cellText: (t) => t.name, sortValue: (t) => t.name },
          { key: 'kind', header: 'Kind', render: (t) => <Badge tone={t.kind === 'document' ? 'accent' : 'neutral'}>{KIND_LABEL[t.kind]}</Badge>, sortValue: (t) => t.kind },
          { key: 'parent', header: 'Base', render: (t) => (t.parent ? <Link to={routes.template(org, gameSlug, branch, t.parent)}><code>{t.parent}</code></Link> : <span className="muted">—</span>), cellText: (t) => t.parent ?? '', sortValue: (t) => t.parent ?? '' },
          { key: 'params', header: 'Parameters', align: 'right', render: (t) => t.effectiveParameters.length, sortValue: (t) => t.effectiveParameters.length },
          { key: 'documentCount', header: 'Documents', align: 'right', render: (t) => <Link to={routes.documents(org, gameSlug, branch, t.name)}>{Number(t.documentCount)}</Link>, cellText: (t) => String(t.documentCount), sortValue: (t) => Number(t.documentCount) },
          { key: 'flags', header: 'Flags', render: (t) => t.flags.join(', '), cellText: (t) => t.flags.join(', ') },
          { key: 'description', header: 'Description', render: (t) => t.description ?? '', cellText: (t) => t.description ?? '' },
          {
            key: 'menu',
            header: '',
            width: 40,
            fixed: true,
            render: (t) => (
              <RowMenu
                t={t}
                canWrite={canWrite}
                onEditor={() => navigate(routes.template(org, gameSlug, branch, t.name))}
                onDocuments={() => navigate(routes.documents(org, gameSlug, branch, t.name))}
                onClone={() => void actions.clone(t)}
                onDelete={() => void actions.remove(t).then((ok) => ok && q.refetch())}
              />
            ),
          },
        ]}
      />
      {actions.jobDialog}
    </section>
  );
}

function RowMenu({ t, canWrite, onEditor, onDocuments, onClone, onDelete }: { t: Template; canWrite: boolean; onEditor: () => void; onDocuments: () => void; onClone: () => void; onDelete: () => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        label={`Actions for ${t.name}`}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        <MoreVertical aria-hidden="true" />
      </IconButton>
      <Menu
        anchor={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        label="Template actions"
        items={[
          { key: 'editor', label: 'Open editor', onSelect: onEditor },
          { key: 'documents', label: 'Open documents', onSelect: onDocuments },
          { key: 'clone', label: `Clone (${copyName(t.name)})`, onSelect: onClone, disabled: !canWrite },
          { key: 's', label: '', separator: true },
          { key: 'delete', label: 'Delete', onSelect: onDelete, danger: true, disabled: !canWrite },
        ]}
      />
    </>
  );
}
